import { useEffect, useMemo, useRef } from 'react';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function asString(value) {
  return typeof value === 'string' ? value : '';
}

function normalizeProgressElements(progressPopover) {
  const safePopover = asObject(progressPopover);

  if (!safePopover.enabled) {
    return [];
  }

  return asArray(safePopover.elements)
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const hasLabel = Object.prototype.hasOwnProperty.call(item, 'label');
      const value = asString(item.value).trim();
      const label = hasLabel ? asString(item.label) : '';
      const type = asString(item.type).trim() || 'text';
      const className = asString(item.className).trim();

      if (!value) {
        return null;
      }

      return {
        hasLabel,
        label,
        value,
        type,
        className,
      };
    })
    .filter(Boolean);
}

function buildFallbackElements({ serverProgress, serverConfidence }) {
  const ratio = Number(serverProgress?.ratio ?? 0);
  const answered = Number(serverProgress?.answered ?? 0);
  const total = Number(serverProgress?.total ?? 0);
  const confidenceLevel = asString(serverConfidence?.level).trim();

  const items = [];

  if (total > 0) {
    items.push({
      hasLabel: true,
      label: 'Core answers',
      value: `${answered} / ${total}`,
      type: 'text',
      className: '',
    });

    items.push({
      hasLabel: true,
      label: 'Completion',
      value: `${Math.round(ratio * 100)}%`,
      type: 'text',
      className: '',
    });
  }

  if (confidenceLevel) {
    items.push({
      hasLabel: true,
      label: 'Insights confidence level',
      value: confidenceLevel,
      type: 'text',
      className: '',
    });
  }

  return items;
}

export default function ProgressPopover({
  serverProgress,
  serverConfidence,
  progressPopover,
  isOpen,
  onToggle,
  onClose,
}) {
  const rootRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    function handlePointerDown(event) {
      if (!rootRef.current?.contains(event.target)) {
        onClose?.();
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        onClose?.();
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const progressPercent = Math.round(Number(serverProgress?.ratio ?? 0) * 100);

  const elements = useMemo(() => {
    const configured = normalizeProgressElements(progressPopover);

    if (configured.length > 0) {
      return configured;
    }

    return buildFallbackElements({
      serverProgress,
      serverConfidence,
    });
  }, [progressPopover, serverProgress, serverConfidence]);

  return (
    <div className="dv-br-progressPopoverAnchor" ref={rootRef}>
      <button
        type="button"
        className="dv-br-progressTrigger"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label="Open progress details"
      >
        <div className="dv-br-topbar__progressLabel">progress</div>

        <div
          className="dv-br-ring"
          style={{ '--dv-br-ring-pct': `${progressPercent}%` }}
          aria-label={`Progress ${progressPercent}%`}
        >
          <div className="dv-br-ring__value">{progressPercent}%</div>
        </div>
      </button>

      {isOpen ? (
        <div
          className="dv-br-progressPopover"
          role="dialog"
          aria-modal="false"
          aria-label="Progress details"
        >
          <div className="dv-br-progressPopover__arrow" />

          <div className="dv-br-progressPopover__card">
            <button
              type="button"
              className="dv-br-progressPopover__close"
              onClick={onClose}
              aria-label="Close progress details"
            >
              ✕
            </button>

            <div className="dv-br-progressPopover__rows">
              {elements.map((item, index) => {
                const rowClassName = [
                  'dv-br-progressPopover__row',
                  item.hasLabel
                    ? 'dv-br-progressPopover__row--withLabel'
                    : 'dv-br-progressPopover__row--full',
                  item.className || '',
                ]
                  .filter(Boolean)
                  .join(' ');

                if (item.type === 'html') {
                  if (item.hasLabel) {
                    return (
                      <div key={index} className={rowClassName}>
                        <span className="dv-br-progressPopover__label">{item.label}</span>
                        <strong
                          className="dv-br-progressPopover__value"
                          dangerouslySetInnerHTML={{ __html: item.value }}
                        />
                      </div>
                    );
                  }

                  return (
                    <div
                      key={index}
                      className={rowClassName}
                      dangerouslySetInnerHTML={{ __html: item.value }}
                    />
                  );
                }

                if (item.hasLabel) {
                  return (
                    <div key={index} className={rowClassName}>
                      <span className="dv-br-progressPopover__label">{item.label}</span>
                      <strong className="dv-br-progressPopover__value">{item.value}</strong>
                    </div>
                  );
                }

                return (
                  <div key={index} className={rowClassName}>
                    <strong className="dv-br-progressPopover__value">{item.value}</strong>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
